import React from "react";
import { useNavigate } from "react-router-dom";
import {
  IconEye,
  IconClipboardFilled,
  IconCircleCheck,
  IconClock,
  IconClockHour4,
  IconMapPin,
  IconCalendar,
  IconRefresh,
} from "@tabler/icons-react";

import { formatDate } from "../../../service/convert/date";
import { IRequestItem } from "../../../store/@types/requests";

interface IRequestCard {
  item: IRequestItem;
}

const RequestCard: React.FC<IRequestCard> = ({ item }) => {
  const navigate = useNavigate();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <IconCircleCheck size={16} className="text-green-600" />;
      case "rejected":
        return <IconClockHour4 size={16} className="text-red-600" />;
      case "in_review":
        return <IconClock size={16} className="text-blue-600" />;
      case "pending":
        return <IconClock size={16} className="text-yellow-600" />;
      default:
        return <IconClock size={16} className="text-gray-600" />;
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ");
  };

  return (
    <>
      <div className="group w-full bg-white rounded-xl shadow-sm border border-gray-100/20 hover:border-primary-200 hover:shadow-md transition-all duration-200 overflow-hidden">
        {/* Header Section */}
        <div className="p-4 pb-3 border-b border-gray-50/20">
          <div className="flex items-start justify-between gap-3">
            {/* Icon and Title */}
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="flex-shrink-0 p-2 bg-primary-50/10 rounded-lg">
                <IconClipboardFilled size={24} className="text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-gray-900 leading-tight mb-1 break-words">
                  {item.service ? item.service.name : item.document?.name}
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                      item.type === "new"
                        ? "bg-primary-50/10 text-primary-700"
                        : "bg-purple-50 text-purple-700"
                    }`}
                  >
                    {item.type === "new" ? (
                      <IconCalendar size={12} />
                    ) : (
                      <IconRefresh size={12} />
                    )}
                    {item.type === "new" ? "New Request" : "Renewal"}
                  </span>
                </div>
              </div>
            </div>

            {/* View Button */}
            <button
              onClick={() => navigate(`/dashboard/requests/${item.id}`)}
              className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-lg text-gray-400 bg-gray-50/10 
                       hover:text-primary-700  transition-all duration-200 active:scale-95"
              aria-label="View request details"
            >
              <IconEye size={20} />
            </button>
          </div>
        </div>

        {/* Body Section */}
        <div className="p-4 space-y-3">
          {/* Location */}
          <div className="flex items-start gap-2">
            <div className="flex-shrink-0 mt-0.5">
              <IconMapPin size={16} className="text-gray-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                LOCATION
              </p>
              <p className="text-sm text-gray-700 font-medium mt-0.5">
                {item.state.name}, {item.state.country.name}
              </p>
            </div>
          </div>

          {/* Date Information */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 mt-0.5">
                <IconCalendar size={14} className="text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                  CREATED
                </p>
                <p className="text-sm text-gray-700 mt-0.5">
                  {formatDate(new Date(item.created_at))}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <div className="flex-shrink-0 mt-0.5">
                <IconRefresh size={14} className="text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                  UPDATED
                </p>
                <p className="text-sm text-gray-700 mt-0.5">
                  {formatDate(new Date(item.created_at))}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Section - Status */}
        <div
          className={`px-4 py-3 border-t border-gray-50/20 flex items-center justify-between`}
        >
          <div className="flex items-center gap-2">
            {getStatusIcon(item.request_status || "pending")}
            <span className="text-sm font-semibold">
              {getStatusLabel(item.request_status || "pending")}
            </span>
          </div>
          <div className="text-xs opacity-75">ID: {item.id.slice(-6)}</div>
        </div>
      </div>
    </>
  );
};

export default RequestCard;
